import React, { useMemo } from 'react';
import collect from 'collect.js';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import Logo from '../../resources/assets/icons/Logo';
import useMaps from '../../hooks/useMaps';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import QrCodeHelpers from '../../services/helpers/QrCodeHelpers';
import usePaperWidth from '../../hooks/usePaperWidth';
import useServiceProviderLogo from '../../hooks/useServiceProviderLogo';
import { DeliveryProvider, OrderMethod, PaymentMethod, TaxBehavior } from '../../services/exports/Constants';
import DeliveryProviderIcon from '../delivery/DeliveryProviderIcon';
import { H3, Paragraph, Section } from './ReceiptTypography';
import { InnerPrinterReceiptElementID } from '../../providers/PrintProvider';
import { useSelector } from 'react-redux';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import { useHelpers } from '../../providers/HelpersProvider';
import classnames from 'classnames';
import { DateTime } from 'luxon';

const ProductContainer = ({ noMargin, underline, children }) => (
  <div
    className={classnames('pb-4 mb-4', {
      '!pb-0 !mb-0': noMargin,
      'border-b border-black': underline,
    })}
  >
    {children}
  </div>
);

const LabelValueContainer = ({ underline, bottomMargin, children, className }) => (
  <div
    className={classnames('flex justify-between', className, {
      'border-b border-black': underline,
      'mb-2': bottomMargin,
    })}
  >
    {children}
  </div>
);

const H2 = ({ noMargin, negativeOffset, center, regular, children }) => (
  <h2
    className={classnames('pb-0 text-black font-semibold text-[34px] leading-[56px]', {
      'mb-0': noMargin,
      'mb-[-6px]': negativeOffset,
      'text-center': center,
      'font-medium': regular,
    })}
  >
    {children}
  </h2>
);

const CenterContainer = ({ children }) => <div className="flex flex-col justify-center items-center">{children}</div>;

export default function InnerPrinterReceipt(props) {
  const { payload: order } = props;

  const { t: translate } = useTranslation(null, { keyPrefix: 'Components:Print:InnerPrinterReceipt' });

  const { company } = useSelector((state) => state.company);

  const lng = company?.legal_lang ?? company?.region?.default_locale;

  const { formatCurrency, formatTime, formatDateTime, formatProductName } = useHelpers();
  const { getGoogleMapsUrl } = useMaps();
  const isMasterServiceProvider = company?.service_provider?.is_master;
  const { isWidePaper, pageWidth } = usePaperWidth();
  const serviceProviderLogo = useServiceProviderLogo(isMasterServiceProvider);

  const { payment_method_id } = order.payment;

  const isCashPayment = payment_method_id === PaymentMethod.Cash;
  const isExternalDelivery = OrderHelpers.isExternalDelivery(order);

  function t(key, config) {
    return translate(key, { lng, ...config });
  }

  const modifierGroups = useMemo(
    () => collect(order?.line_items).pluck('modifier_groups').collapse().unique('id').keyBy('id'),
    [order?.line_items],
  );

  const renderLogo = () => {
    if (isMasterServiceProvider) {
      return (
        <div className="flex items-center justify-center">
          <Logo color="black" width={pageWidth * 0.4} />
          {isExternalDelivery && (
            <DeliveryProviderIcon
              provider={order.delivery_provider}
              width={pageWidth * 0.4}
              color="black"
              className="ml-2"
            />
          )}
        </div>
      );
    }

    if (!serviceProviderLogo) {
      return null;
    }

    return (
      <div className="flex items-center justify-center">
        <img
          src={serviceProviderLogo}
          alt=""
          style={{ width: pageWidth * 0.4, maxHeight: pageWidth * 0.25, objectFit: 'contain' }}
        />
      </div>
    );
  };

  const renderCustomerInfo = () => (
    <Section>
      <H2 negativeOffset>{order?.is_dummy ? t('customer_info.dummy_order') : `#${order?.number}`}</H2>
      <H2>
        {order.customer_first_name} {order.customer_last_name}
      </H2>
      <Paragraph wide={isWidePaper}>Tel: {order.customer_phone_number}</Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('customer_info.ordered_at')} {formatDateTime(order?.created_at, { dateFormat: 'dd/MM/yyyy' }, lng)}
      </Paragraph>
    </Section>
  );

  const renderDeliveryInfo = () => (
    <div className="mt-2">
      <H3>{t('delivery_info.info')}</H3>
      {AddressHelpers.lines(
        {
          street_name: order?.delivery_street_name,
          street_number: order?.delivery_street_number,
          zip_code: order?.delivery_zip_code,
          city: order?.delivery_city,
        },
        company?.region?.address_format,
      ).map((line) => (
        <Paragraph key={line} wide={isWidePaper}>
          {line}
        </Paragraph>
      ))}
      {order?.delivery_doorbell_name && <Paragraph wide={isWidePaper}>{order?.delivery_doorbell_name}</Paragraph>}
      {order?.delivery_company_name && <Paragraph wide={isWidePaper}>{order?.delivery_company_name}</Paragraph>}
      {order?.customer_note && (
        <Paragraph wide={isWidePaper}>
          {t('delivery_info.note')}
          {'" '}
          {order?.customer_note}
          {'" '}
        </Paragraph>
      )}
    </div>
  );

  const renderScheduleInfo = () => {
    function getOrderMethod() {
      switch (order?.method) {
        case OrderMethod.Delivery:
          return t('schedule_info.delivery');
        case OrderMethod.OnSite:
          return (
            t(`schedule_info.room_service_location.${order?.room_service_location?.type}`, {
              details: order?.room_service_details,
            }) + (order?.room_service_details ? ` #${order?.room_service_details}` : '')
          );
        default:
          return t('schedule_info.pickup');
      }
    }

    // text-lg is 24px (Theme.js), which already clears the floor — but on wide paper it would
    // leave the most-scanned line on the receipt smaller than the product lines.
    const timeLineSize = isWidePaper ? 'text-[26px] leading-[34px]' : 'text-lg';

    return (
      <Section borderTop>
        <div className="flex flex-col">
          <H2 center noMargin negativeOffset>
            {getOrderMethod()}
          </H2>
          {order?.room_service_location && (
            <>
              <Paragraph center wide={isWidePaper}>
                {order?.room_service_location?.name}
              </Paragraph>
              <Paragraph center wide={isWidePaper}>
                {AddressHelpers.format(order?.room_service_location ?? {}, company?.region?.address_format)}
              </Paragraph>
            </>
          )}
          {order?.scheduled_for && !DateTime.fromISO(order?.scheduled_for).hasSame(DateTime.local(), 'day') && (
            <p className={classnames('my-0 py-0 text-center font-semibold text-black italic', timeLineSize)}>
              {formatDateTime(order?.scheduled_for, { format: 'cccc, dd LLL' }, lng)}
            </p>
          )}
          {order?.scheduled_for ? (
            <p className={classnames('my-0 py-0 text-center text-black italic', timeLineSize)}>
              {t('schedule_info.wish_time')} {formatTime(order?.scheduled_for)}
            </p>
          ) : null}
          <p className={classnames('my-0 py-0 text-center text-black italic', timeLineSize)}>
            {isExternalDelivery ? t('schedule_info.driver_arrival_time') : t('schedule_info.confirmed_time')}{' '}
            <span className="my-0 py-0 font-semibold">{formatTime(order?.estimated_pickup_time)}</span>
          </p>
          {isExternalDelivery && (
            <CenterContainer>
              <Paragraph center italic wide={isWidePaper}>
                {t('schedule_info.external_delivery', { carrier: t(`delivery_providers.${order.delivery_provider}`) })}
              </Paragraph>
            </CenterContainer>
          )}
        </div>
        {order?.order_instructions?.length > 0 && (
          <Paragraph wide={isWidePaper}>
            {t('schedule_info.order_instructions')} {order?.order_instructions}
          </Paragraph>
        )}
        {order?.method === OrderMethod.Delivery && renderDeliveryInfo()}
      </Section>
    );
  };

  /**
   * A heading opens each run of options coming from the same group. The backend stores a line
   * item's modifiers in menu order, which keeps one group's options adjacent, so heading once per
   * run labels every group exactly once — nested groups included, where any nesting used to
   * suppress every heading on the item.
   */
  const renderModifierList = ({ modifiersByParent, modifiers, isNested = false, parentQuantity }) =>
    modifiers
      .map((modifier, index) =>
        renderModifier({
          modifiersByParent,
          modifier,
          isNested,
          parentQuantity,
          hasTitleGroup:
            !company?.is_modifier_group_hidden_on_receipt &&
            modifiers.get(index - 1)?.modifier_group_id !== modifier.modifier_group_id,
        }),
      )
      .all();

  const renderModifier = ({ modifiersByParent, modifier, isNested = false, hasTitleGroup, parentQuantity }) => {
    const modifierGroupName = modifierGroups.get(modifier.modifier_group_id)?.name;
    const quantity = modifier.quantity * parentQuantity;
    const price = quantity * (modifier?.actual_price ?? 0);

    return (
      <div className="pl-2" key={`line-item-modifier-${modifier.uuid}`}>
        {hasTitleGroup && modifierGroupName && (
          <Paragraph big wide={isWidePaper}>
            {modifierGroupName}
          </Paragraph>
        )}
        <div className="pl-2">
          <LabelValue>
            <div className="flex">
              <Paragraph big rightPadding wide={isWidePaper}>
                {isNested ? '+' : '•'}
              </Paragraph>
              <Paragraph big wide={isWidePaper}>
                {quantity > 1 && <span className="font-bold">{quantity}x</span>}
                &nbsp;
                {modifier.name}
              </Paragraph>
            </div>
            <Paragraph wide={isWidePaper}>{formatCurrency(price)}</Paragraph>
          </LabelValue>
          {renderNestedModifiers(modifiersByParent, modifier.uuid, parentQuantity * modifier.quantity)}
        </div>
      </div>
    );
  };

  const renderNestedModifiers = (modifiersByParent, modifierUuid, parentQuantity) => {
    const nestedModifiers = modifiersByParent.get(modifierUuid);

    if (!nestedModifiers?.count()) {
      return;
    }

    return renderModifierList({ modifiersByParent, modifiers: nestedModifiers, isNested: true, parentQuantity });
  };

  const renderProducts = () => (
    <Section borderTop>
      <H3>{t('products.item')}</H3>
      {order?.line_items?.map((lineItem, index) => {
        const isEndOfList = order?.line_items && index === (order?.line_items?.length ?? 0) - 1;
        const modifiersByParent = collect(lineItem.modifiers).groupBy('parent_uuid');
        const rootModifiers = modifiersByParent.get('');

        return (
          <ProductContainer noMargin={isEndOfList} underline={!isEndOfList} key={`line-item-${lineItem.id}`}>
            <LabelValue
              containerProps={{
                bottomMargin: true,
              }}
            >
              <Paragraph bold big wide={isWidePaper}>
                {lineItem?.quantity}x {formatProductName(lineItem)}
              </Paragraph>
              <Paragraph bold wide={isWidePaper}>
                {formatCurrency(lineItem?.product_actual_price)}
              </Paragraph>
            </LabelValue>
            {lineItem?.note && (
              <Paragraph wide={isWidePaper}>
                {'> >'} {lineItem.note}
              </Paragraph>
            )}
            {rootModifiers &&
              renderModifierList({
                modifiersByParent,
                modifiers: rootModifiers,
                parentQuantity: lineItem.quantity,
              })}
          </ProductContainer>
        );
      })}
    </Section>
  );

  const renderPaymentBreakDown = () =>
    !company?.is_price_section_hidden_on_receipt && (
      <Section borderTop>
        <H3>{t('payment_breakdown.price')}</H3>
        <LabelValue>
          <Paragraph wide={isWidePaper}>{t('payment_breakdown.subtotal')}</Paragraph>
          <Paragraph wide={isWidePaper}>{formatCurrency(order?.original_price)}</Paragraph>
        </LabelValue>
        {order?.method_fee > 0 && (
          <LabelValue>
            <Paragraph wide={isWidePaper}>{t('payment_breakdown.delivery_fee')}</Paragraph>
            <Paragraph wide={isWidePaper}>{formatCurrency(order?.method_fee)}</Paragraph>
          </LabelValue>
        )}
        {order?.tip > 0 && (
          <LabelValue>
            <Paragraph wide={isWidePaper}>{t('payment_breakdown.tips')}</Paragraph>
            <Paragraph wide={isWidePaper}>{formatCurrency(order?.tip)}</Paragraph>
          </LabelValue>
        )}
        {order?.service_fee > 0 && (
          <LabelValue>
            <Paragraph wide={isWidePaper}>{t('payment_breakdown.processing_fee')}</Paragraph>
            <Paragraph wide={isWidePaper}>{formatCurrency(order?.service_fee)}</Paragraph>
          </LabelValue>
        )}
        {order?.payment_method_upcharge_fee > 0 && (
          <LabelValue>
            <Paragraph wide={isWidePaper}>
              {t(`payment_breakdown.payment_method_upcharge_fee.${payment_method_id}`)}
            </Paragraph>
            <Paragraph wide={isWidePaper}>{formatCurrency(order?.payment_method_upcharge_fee)}</Paragraph>
          </LabelValue>
        )}
        {order?.total_discount > 0 && <DiscountBreakdown data={order} wide={isWidePaper} />}
        {order?.tax_behaviour === TaxBehavior.Exclusive && order.tax > 0 && (
          <LabelValue containerProps={{ className: 'mt-md' }}>
            <Paragraph wide={isWidePaper}>{t('payment_breakdown.tax')}</Paragraph>
            <Paragraph wide={isWidePaper}>{formatCurrency(order.tax)}</Paragraph>
          </LabelValue>
        )}
        <LabelValue>
          <Paragraph bold wide={isWidePaper}>
            {isCashPayment ? t('payment_breakdown.total') : t('payment_breakdown.paid_amount')}
          </Paragraph>
          <Paragraph bold wide={isWidePaper}>
            {formatCurrency(order?.total_amount)}
          </Paragraph>
        </LabelValue>
        {isCashPayment ? (
          <CenterContainer>
            <H3 center noUnderline noMargin>
              {t('payment_breakdown.order_not_paid')}
            </H3>
            <H3 center noUnderline regular noMargin>
              {t('payment_breakdown.cash')}
            </H3>
          </CenterContainer>
        ) : (
          <CenterContainer>
            <H3 center noUnderline noMargin>
              {t('payment_breakdown.order_paid_online')}
            </H3>
          </CenterContainer>
        )}
        <CenterContainer>
          <Paragraph center wide={isWidePaper}>
            {t('payment_breakdown.this_is_not_an_invoice')}
          </Paragraph>
        </CenterContainer>
      </Section>
    );

  const renderDeliveryQrCode = () => {
    if (order?.method !== OrderMethod.Delivery || order?.delivery_provider !== DeliveryProvider.Standalone) {
      return null;
    }

    const url = getGoogleMapsUrl({
      street_name: order?.delivery_street_name,
      street_number: order?.delivery_street_number,
      zip_code: order?.delivery_zip_code,
      city: order?.delivery_city,
      country: company?.country,
      address_format: company?.region?.address_format,
    });

    if (!url) {
      return null;
    }

    const width = Math.floor(pageWidth);
    const size = QrCodeHelpers.pixelAlignedSize(url, width * 0.6);

    return (
      <Section borderTop>
        {/* Offset floored rather than flex-centred: a half-pixel x would blur every module again. */}
        <div className="mb-2" style={{ marginLeft: `${Math.floor((width - size) / 2)}px` }}>
          <QRCode size={size} value={url} />
        </div>
        <Paragraph center noMargin wide={isWidePaper}>
          {t('delivery_qr_code.scan')}
        </Paragraph>
      </Section>
    );
  };

  return (
    <div
      className="bottom-0 box-border fixed pt-[50px] pb-[130px]"
      id={InnerPrinterReceiptElementID}
      style={{
        zIndex: 0,
        width: `${pageWidth}px`,
        minWidth: `${pageWidth}px`,
        maxWidth: `${pageWidth}px`,
        left: `-${pageWidth}px`,
      }}
    >
      {renderLogo()}
      {renderCustomerInfo()}
      {renderScheduleInfo()}
      {renderProducts()}
      {renderPaymentBreakDown()}
      {renderDeliveryQrCode()}
    </div>
  );
}

function DiscountBreakdown({ data, wide }) {
  const { t: translate } = useTranslation(null, {
    keyPrefix: 'Components:Print:InnerPrinterReceipt.discount_breakdown',
  });
  const companyLng = useSelector((state) => state.company.company?.legal_lang);
  const lng = companyLng ?? 'de';
  const { formatCurrency } = useHelpers();

  if (!data?.discount_breakdown) {
    return null;
  }

  const entries = Object.entries(data?.discount_breakdown);

  if (!entries?.length) {
    return null;
  }

  function t(key, config) {
    return translate(key, { lng, ...config });
  }

  return (
    <>
      <LabelValue>
        <Paragraph wide={wide}>{t('label')}</Paragraph>
        <Paragraph wide={wide}>-{formatCurrency(data?.total_discount)}</Paragraph>
      </LabelValue>
      {entries.map((entry) => (
        // `sub` keeps these entries a step below a regular line — the size lives in Paragraph
        // rather than in a `[&_*]:text-sm` descendant-selector override on the container.
        <LabelValue key={entry[0]} containerProps={{ className: 'ml-md' }}>
          <Paragraph sub wide={wide}>
            {t(entry[0])}
          </Paragraph>
          <Paragraph sub wide={wide}>
            -{formatCurrency(entry[1] / 100)}
          </Paragraph>
        </LabelValue>
      ))}
    </>
  );
}

function LabelValue({ children, containerProps }) {
  return (
    <LabelValueContainer {...containerProps}>
      <div className="pr-2 flex-shrink">{children[0]}</div>
      <div className="flex-none">{children[1]}</div>
    </LabelValueContainer>
  );
}
